/**
 * Display-name shape shared by the nursery-members query result.
 *
 * Lifted out of `components/event-records/fire-drill.tsx` when NAU-342 gave the
 * acknowledgement-request picker a second use for it: the helper is generic to
 * nursery members and is not fire-drill-specific. As a `.ts` module its fallback
 * behaviour is also unit-testable, which it was not inside a `.tsx` component
 * (vitest only collects `.ts` here).
 */
/**
 * The fields a display name is derived from; structural so every surface that
 * shows who did something spells the name the same way.
 */
export interface DisplayNameFields {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

export interface NurseryMemberOption extends DisplayNameFields {
  userId: string;
}

/** Member's display name, falling back to email when no name is set. */
export function memberDisplayName(member: DisplayNameFields): string {
  return (
    [member.firstName, member.lastName].filter(Boolean).join(" ").trim() ||
    member.email
  );
}

/**
 * Nursery roles that may be asked to acknowledge an event record (NAU-342).
 *
 * Mirrors ACKNOWLEDGEMENT_REQUEST_ELIGIBLE_NURSERY_ROLES server-side. Narrower than
 * "any member" because only these roles can READ someone else's event record —
 * asking anyone else creates a request they can never see, on a record that then
 * sits in Awaiting acknowledgement with no way to clear it. The API rejects them
 * too; filtering here keeps them out of the picker rather than surfacing a 400.
 */
export const ACKNOWLEDGEMENT_REQUEST_ELIGIBLE_ROLES = [
  "MANAGER",
  "DEPUTY_MANAGER",
] as const;

export function canBeAskedToAcknowledge(role: string | undefined): boolean {
  if (!role) {
    return false;
  }
  return (ACKNOWLEDGEMENT_REQUEST_ELIGIBLE_ROLES as readonly string[]).includes(
    role,
  );
}
